import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { fetchingCardTypeAtom } from "checkout-experience/atoms/payment-atoms";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { useIsCreditCardWithSurcharge } from "checkout-query-client/hooks/use-is-credit-card-with-surcharge";
import { UseQueryResult } from "checkout-query-client/main";

export const useIsCreditCardWithSurchargeChex = ({
  watch,
  getFieldState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Needed for RHF to subscribe to touched fields properly
  formState: { touchedFields },
}: UseFormReturn): UseQueryResult<boolean, Error> & {
  isFetchingTooLong: boolean;
} => {
  const [isFetchingTooLong, setIsFetchingTooLong] = useState(false);
  const [fieldValidAtLeastOnce, setFieldIsValidAtLeastOnce] = useState(false);
  const setFetchingCardType = useSetAtom(fetchingCardTypeAtom);

  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};

  const {
    merchantSettings,
    experienceType,
    checkoutSession,
    apiHost,
    checkoutSessionToken,
    target,
    version = "",
  } = useChexContext();

  const { invalid, isDirty, isTouched } = getFieldState(`${formId}.cardNumber`);

  useEffect(() => {
    if (
      !invalid &&
      // Difference in behavior due to field frame implementation
      ((experienceType !== "dropInUI" && isTouched) ||
        (experienceType === "dropInUI" && isDirty)) &&
      !fieldValidAtLeastOnce
    ) {
      setFieldIsValidAtLeastOnce(true);
    }
  }, [invalid, isDirty, isTouched, experienceType]);

  const query = useIsCreditCardWithSurcharge({
    apiHost,
    checkoutSessionToken,
    cardNumber: watch(`${formId}.cardNumber`) as string,
    experienceType,
    encryptionKey: merchantSettings?.encryptionKey,
    checkoutSession,
    target,
    fieldIsValid: fieldValidAtLeastOnce,
    merchantSurchargeFeeEnabled: merchantSettings?.surchargeFeeEnabled,
    paymentType,
    version,
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (query.isFetching) {
      timer = setTimeout(() => {
        if (query.isFetching) setIsFetchingTooLong(true);
      }, 2000);
    } else {
      setIsFetchingTooLong(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [query.isFetching]);

  useEffect(() => {
    if (query.status === "success") {
      setFetchingCardType(false);
    }
  }, [query.status]);

  return { ...query, isFetchingTooLong };
};
